<template>
  <b-popover
    :target="'timeline-point-' + point.id"
    triggers="hover"
    placement="rightbottom"
    custom-class="custom-tooltip p-0 overflow-hidden"
  >
    <div
      class="fleet-popover-header"
      :style="{ backgroundColor: point.visit_status.background_color }"
    >
      <span class="mc-text-status">{{ pointStatus(point.visit_status?.raw_type) }}</span>
    </div>
    <hr class="separator" />
    <div class="popover-item fleet-popover-body">
      <div class="">
        <p class="mb-0">
          {{ pointLocationNumber(point.plan_sequence) }}
        </p>
        <p class="mt-1 mb-0">
          <strong>{{ point.address?.name || point.address?.full_name }}</strong>
        </p>
      </div>
    </div>
    <template v-if="showArrivalInfo">
      <hr class="separator" />
      <div class="popover-item fleet-popover-body">
        <div class="fleet-item-container">
          <span class="__header mb-2">{{ $t('fleetTaskInstant.text.arrivalTime') }}</span>
          <div class="__body">
            <span class="__label">Target: </span>
            <span>{{ formatDateTime(point.arrival_time.target) }}</span>
            <span class="__label">{{ $t('fleetTaskInstant.text.actual') }}:</span>
            <span>{{ formatDateTime(point.arrival_time.actual) }}</span>
          </div>
          <span
            v-if="point.arrival_time.late !== null"
            class="late-fleet-info"
            :style="{ color: point.visit_status.background_color }"
            ><div
              class="dot"
              :style="{ backgroundColor: point.visit_status.background_color }"
            ></div>
            {{ `${$t('fleetTaskInstant.text.late')} ${constructLateMessage(point.arrival_time.late)}` }}
            </span>
        </div>
      </div>
    </template>
    <template v-if="showDepartureInfo">
      <hr :class="{ separator: true, 'separator--dashed': showArrivalInfo }" />
      <div class="popover-item fleet-popover-body">
        <div class="fleet-item-container">
          <span class="__header mb-2">{{ $t('fleetTaskInstant.text.departureTime') }}</span>
          <div class="__body">
            <span class="__label">Target:</span>
            <span>{{ formatDateTime(point.departure_time.target) }}</span>
            <span class="__label">{{ $t('fleetTaskInstant.text.actual') }}:</span>
            <span>{{ formatDateTime(point.departure_time.actual) }}</span>
          </div>
        </div>
        <span
          v-if="point.departure_time.late !== null"
          class="late-fleet-info"
          :style="{ color: point.visit_status.background_color }"
          ><div class="dot" :style="{ backgroundColor: point.visit_status.background_color }"></div>
          {{ `${$t('fleetTaskInstant.text.late')} ${constructLateMessage(point.departure_time.late)}` }}
          </span>
      </div>
    </template>
  </b-popover>
</template>

<script>
import { formatIndexDate, parseISO8601Duration } from '@/helpers/tzDate'
import { statusIdToLabelMap } from '../lib/constants/point-status'

export default {
  name: 'FleetTooltip',
  props: {
    point: {
      required: true,
    },
  },
  computed: {
    showArrivalInfo() {
      return this.point.point_type !== 'START'
    },
    showDepartureInfo() {
      return this.point.point_type !== 'END'
    },
    pointStatus() {
      return (rawStatus) => this.$t(statusIdToLabelMap[rawStatus])
    },
    pointLocationNumber() {
      return (sequenceNumber) => {
        const currentLocale = this.$i18n.locale

        let number = sequenceNumber
        if (currentLocale === 'en') {
          if (sequenceNumber == 1) {
            number = `${sequenceNumber}st`
          } else if (sequenceNumber == 2) {
            number = `${sequenceNumber}nd`
          } else if (sequenceNumber == 3) {
            number = `${sequenceNumber}rd`
          } else {
            number = `${sequenceNumber}th`
          }
        }

        return this.$t('fleetTaskInstant.text.pointLocationNumber',  { number: number })
      }
    }
  },
  methods: {
    formatDateTime(dateTime) {
      return dateTime ? `${formatIndexDate(dateTime).date} ${formatIndexDate(dateTime).time}` : '-'
    },
    constructLateMessage(late) {
      if (!late) {
        return ''
      }
      const parsedDate = parseISO8601Duration(late)

      let message = []
      if (parsedDate.days !== null) {
        message.push(`${parsedDate.days} ${this.$t('fleetTaskInstant.time.day')}`)
      }
      if (parsedDate.hours !== null) {
        message.push(`${parsedDate.hours} ${this.$t('fleetTaskInstant.time.hour')}`)
      }
      if (parsedDate.minutes !== null) {
        message.push(`${parsedDate.minutes} ${this.$t('fleetTaskInstant.time.minute')}`)
      }
      if (parsedDate.seconds !== null && message.length === 0) {
        message.push(`${parsedDate.seconds} ${this.$t('fleetTaskInstant.time.second')}`)
      }

      return message.join(' ')
    },
  },
}
</script>

<style lang="scss" scoped>
// TODO refactor css
.custom-tooltip {
  background-color: $popover-bg !important;
  color: $mep-color-text !important;
  width: 250px !important;
  border: 1px solid $teal-100;
  box-shadow: 0 4px 8px rgba(16, 24, 64, 0.08) !important;

  font-family: $font-family-sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 0;
}

.fleet-popover-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  color: $mep-color-text;

  .__status-dots {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .mc-text-status {
    color: $white;
  }
}

.fleet-popover-body {
  > div {
    padding: 6px 0;
  }

  .late-fleet-info {
    font-size: 11px;
    font-weight: 400;
    display: flex;
    align-items: center;

    .dot {
      height: 6px;
      width: 6px;
      border-radius: 100vh;
      margin-right: 4px;
    }
  }
}

.separator {
  border-top: 1px solid $gray-600;
  margin: 0;

  &--dashed {
    border-top: 1px dashed $gray-600;
  }
}

.popover-item {
  padding: 4px 10px;
}

.fleet-item-container {
  color: $mep-color-text-400;
  display: grid;
  row-gap: 2px;

  .__header {
    font-size: 11px;
  }

  .__body {
    display: grid;
    grid-template-columns: 0.5fr 2fr;
    row-gap: 2px;

    .__label {
      color: $mep-color-text-300;
    }
  }
}
</style>
